import React from 'react';
import Button from '@hero/ui-kit/inputs/Button';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';

import { useUserStatusContext } from '../../../context/UserStatus';
import type { useImportMinutes } from '../api/useCareWrites';
import { asRefusal, careErrorText } from '../constants';
import * as Style from '../style.module.scss';
import { ImportSummary } from '../types';
import { dateTime } from '../presentation';

const example = (month: string) =>
    `First Name,Last Name,DOB,PT ID,Provider group,Documenting User,Work Date,RPM Minutes,CCM Minutes,RTM MSK Minutes,RTM RESP Minutes,BHI Minutes,APCM Minutes\nSynthetic,Example,01/01/70,,,,${month.slice(5, 7)}/15/${month.slice(2, 4)},00:00,21:48,10:00,10:00,00:00,00:00\n`;

const MinutesImport: React.FC<{
    month: string;
    disabled: boolean;
    mutation: ReturnType<typeof useImportMinutes>;
}> = ({ month, disabled, mutation }) => {
    const { userStatus } = useUserStatusContext();
    const actorName = (actor?: string) => {
        if (!actor || actor === 'unknown') return 'Name not recorded';
        if (actor === `hero_user:${userStatus?.hero_user_id}`) {
            return (
                [userStatus?.first_name, userStatus?.last_name].filter(Boolean).join(' ').trim() ||
                userStatus?.email ||
                'You'
            );
        }
        if (/^hero_user:\d+$/.test(actor)) return 'Team member';
        if (/^(system|cron|scheduled|care_|hwm23270-synthetic|cli:)/.test(actor)) return 'System';
        return 'Name not available';
    };
    const input = React.useRef<HTMLInputElement>(null);
    const [file, setFile] = React.useState<File>();
    const failure = asRefusal(mutation.error);
    const summary = (mutation.data ?? failure?.data) as Partial<ImportSummary> | undefined;
    const busy = disabled || mutation.isPending;
    return (
        <div className={`${Style.formModal} ${Style.importContent}`}>
            <header className={Style.dialogHeader}>
                <H role="h5" noDefaultMargin>
                    Import patient minutes
                </H>
                <P size="small" noDefaultMargin>
                    Service month: {month}
                </P>
            </header>
            <P>
                This CSV replaces the active minutes for {month}. Include the complete month-to-date
                dataset; previous imports stay in history.{' '}
                Missing patients or enrolled programs without CSV rows are flagged for review.{' '}
                Minutes cannot be edited here.
            </P>
            <P size="small">
                Start with the column-header row. DOB and Work Date are MM/DD/YY and all durations
                are MM:SS (21:48 means 21 minutes, 48 seconds). Patient matching uses first name,
                last name and DOB.
            </P>
            <P size="small">
                RTM MSK and RTM RESP time are combined. Conditions and ICD codes stay visible in the
                patient table; this file does not need diagnosis columns.
            </P>
            <a
                href={`data:text/csv;charset=utf-8,${encodeURIComponent(example(month))}`}
                download="synthetic-patient-minutes-example.csv"
            >
                Download a synthetic example CSV
            </a>
            <div className={Style.filePicker}>
                <label htmlFor="care-minutes-file">
                    <strong>Complete monthly CSV</strong>
                </label>
                <P size="small" noDefaultMargin>
                    Choose the provider's CSV file for this month.
                </P>
                <input
                    id="care-minutes-file"
                    ref={input}
                    type="file"
                    accept=".csv,text/csv"
                    disabled={busy}
                    onChange={(event) => {
                        setFile(event.target.files?.[0]);
                        mutation.reset();
                    }}
                />
            </div>
            <div className={Style.dialogActions}>
                <Button
                    size="small"
                    variant="primary"
                    width="natural"
                    noDefaultMargin
                    disabled={busy || !file}
                    onClick={() => {
                        if (!file) return;
                        mutation.mutate(
                            { file, rematch: true },
                            {
                                onSuccess: () => {
                                    setFile(undefined);
                                    if (input.current) input.current.value = '';
                                }
                            }
                        );
                    }}
                >
                    {mutation.isPending ? 'Importing…' : 'Import minutes'}
                </Button>
            </div>
            {mutation.isPending ? (
                <P size="small" role="status">
                    Uploading and matching patient minutes. This dialog will be available to close
                    when the import finishes.
                </P>
            ) : null}
            {mutation.error ? (
                <P className={Style.apiError} role="alert">
                    {careErrorText(mutation.error)}
                </P>
            ) : null}
            {summary ? (
                <div role="status" className={Style.importResult}>
                    <P noDefaultMargin>{`Import ${summary.import_id ?? '—'} · ${summary.outcome ?? 'Refused'}`}</P>
                    <P size="small" noDefaultMargin>{`Matched rows: ${summary.matched_count ?? '—'} · Unmatched rows: ${summary.unmatched_count ?? '—'} · Ambiguous rows: ${summary.ambiguous_count ?? '—'} · Missing patients: ${summary.missing_patient_count ?? '—'}`}</P>
                    <P size="small" noDefaultMargin>
                        Uploaded by {actorName(summary.received_by)} ·{' '}
                        {dateTime(summary.received_at)}.
                    </P>
                    <P size="small" noDefaultMargin>
                        Matched by {actorName(summary.actor)} · {dateTime(summary.matched_at)}.
                    </P>
                    {summary.ambiguous_source_rows?.length ? (
                        <P size="small" noDefaultMargin>
                            Ambiguous source row numbers: {summary.ambiguous_source_rows.join(', ')}
                        </P>
                    ) : null}
                    <P size="small" noDefaultMargin>
                        After importing minutes, select Calculate billable codes to update the
                        results.
                    </P>
                </div>
            ) : null}
        </div>
    );
};

export default MinutesImport;
